import { useState, useEffect, useRef } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "";

/**
 * AdSlot — drop this anywhere on the public site.
 *
 * Usage:
 *   <AdSlot placement="homepage_banner" width={728} height={90} />
 *   <AdSlot placement="course_sidebar"  width={300} height={250} />
 *   <AdSlot placement="footer_banner"   width={728} height={90} />
 *
 * It fetches all active ads for the given placement,
 * then rotates through them every `intervalMs` milliseconds.
 * If no ads are active, it renders nothing.
 */
export default function AdSlot({ placement, width = 728, height = 90, intervalMs = 5000 }) {
  const [ads,     setAds]     = useState([]);
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const res  = await fetch(`${API_BASE}/api/ads/active?placement=${placement}`);
        const json = await res.json();
        if (json.success && json.data.length > 0) {
          setAds(json.data);
        }
      } catch {
        // Silently fail — no ads shown
      }
    })();
  }, [placement]);

  useEffect(() => {
    if (ads.length <= 1) return;
    timerRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % ads.length);
    }, intervalMs);
    return () => clearInterval(timerRef.current);
  }, [ads, intervalMs]);

  // Nothing to show
  if (ads.length === 0) return null;

  const ad = ads[current];

  return (
    <div
      style={{
        width: "100%",
        maxWidth: `${width}px`,
        height: `${height}px`,
        margin: "0 auto",
        position: "relative",
        overflow: "hidden",
        borderRadius: "4px",
      }}
    >
      {ads.map((a, i) => (
        <a
          key={a._id}
          href={a.linkUrl || "#"}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            position: "absolute",
            inset: 0,
            opacity: i === current ? 1 : 0,
            transition: "opacity 0.6s ease",
            display: "block",
          }}
        >
          <img
            src={a.imageUrl}
            alt={`${a.company} advertisement`}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        </a>
      ))}

      {/* Dot indicators (only if multiple ads) */}
      {ads.length > 1 && (
        <div style={{ position: "absolute", bottom: "6px", right: "8px", display: "flex", gap: "4px" }}>
          {ads.map((_, i) => (
            <div
              key={i}
              onClick={() => setCurrent(i)}
              style={{
                width: i === current ? "16px" : "6px",
                height: "6px",
                borderRadius: "3px",
                background: i === current ? "#B8963E" : "rgba(255,255,255,0.4)",
                cursor: "pointer",
                transition: "all 0.3s",
              }}
            />
          ))}
        </div>
      )}

      {/* "Advertisement" label */}
      <div style={{ position: "absolute", top: "3px", left: "6px", fontSize: "9px", color: "rgba(255,255,255,0.4)", fontFamily: "sans-serif", letterSpacing: "0.05em", textTransform: "uppercase", pointerEvents: "none" }}>
        Ad
      </div>
    </div>
  );
}